import { FormattedMessage, useIntl } from "react-intl";

import { LinkButton } from "@atlaskit/button-next/new";
import Heading from "@atlaskit/heading";
import {
    Box,
    Flex,
    xcss,
    Text,
    Inline,
    Stack,
    Anchor
} from "@atlaskit/primitives-next";

import {
    FASTSHIFT_PORTAL_MARKETING_URL,
    BASE_URL,
    SAC_URL,
    IDENTITY_BASE_URL
} from "@/components/fastshift/src/common/routes/constants";

import { PadlockIcon } from "../../assets/padlock-icon";
import { useFastshiftPageAnalytics, useFastshiftUIAnalytics } from "../hooks";

import { messages } from "./messages";

const padlockStyles = xcss({
    marginBlockEnd: "space.150",
    paddingBlock: "space.300"
});

const textStyles = xcss({
    maxWidth: "580px",
    marginBlockEnd: "space.200"
});

const SWITCH_ACCOUNT_IDENTITY_URL = `${IDENTITY_BASE_URL}/login?continue=${encodeURIComponent(
    `${BASE_URL}/fastshift`
)}&prompt=select_account`;

export const NotEligibleDisplay = ({
    ineligibilityReason
}: {
    ineligibilityReason: "no-license" | "not-linked";
}) => {
    useFastshiftPageAnalytics();
    const {
        sendGoBackButtonClickUIEvent,
        sendSignInWithAnotherAccountButtonClickUIEvent
    } = useFastshiftUIAnalytics();

    return (
        <Box padding="space.300">
            <Flex
                justifyContent="center"
                alignItems="center"
                direction="column"
            >
                <Box xcss={padlockStyles}>
                    <PadlockIcon />
                </Box>
                <Stack space="space.200" alignInline="center" xcss={textStyles}>
                    <Heading size="small">
                        <FormattedMessage {...messages.notEligibleTitle} />
                    </Heading>

                    <Text align="center">
                        <DescriptionMessage
                            ineligibilityReason={ineligibilityReason}
                        />
                    </Text>
                </Stack>
                <Inline space="space.200">
                    <LinkButton
                        href={FASTSHIFT_PORTAL_MARKETING_URL}
                        onClick={() => sendGoBackButtonClickUIEvent()}
                    >
                        <FormattedMessage {...messages.goBackButton} />
                    </LinkButton>
                    <LinkButton
                        href={SWITCH_ACCOUNT_IDENTITY_URL}
                        appearance="primary"
                        onClick={() =>
                            sendSignInWithAnotherAccountButtonClickUIEvent()
                        }
                    >
                        <FormattedMessage
                            {...messages.signInWithAnotherAccountButton}
                        />
                    </LinkButton>
                </Inline>
            </Flex>
        </Box>
    );
};

const DescriptionMessage = ({
    ineligibilityReason
}: {
    ineligibilityReason: "no-license" | "not-linked";
}) => {
    const { formatMessage } = useIntl();
    const { sendContactSupportLinkClickUIEvent } = useFastshiftUIAnalytics();

    switch (ineligibilityReason) {
        case "no-license":
            return (
                <FormattedMessage
                    {...messages.noLicenseDescription}
                    values={{
                        contactSupportLink: (
                            <Anchor
                                href={`${SAC_URL}/contact`}
                                target="_blank"
                                onClick={() =>
                                    sendContactSupportLinkClickUIEvent()
                                }
                            >
                                {formatMessage(
                                    messages.contactSupportAnchorText
                                )}
                            </Anchor>
                        )
                    }}
                />
            );
        case "not-linked":
            return (
                <FormattedMessage
                    {...messages.notLinkedDescription}
                    values={{
                        contactSupportLink: (
                            <Anchor
                                href={`${SAC_URL}/contact`}
                                target="_blank"
                                onClick={() =>
                                    sendContactSupportLinkClickUIEvent()
                                }
                            >
                                {formatMessage(
                                    messages.contactSupportAnchorText
                                )}
                            </Anchor>
                        )
                    }}
                />
            );
        default:
            return null;
    }
};
